import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { HeartHandshake, Timer, CheckCircle2, Pencil, XCircle } from 'lucide-react';
import { isoDate } from '../../utils/time';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PageHead, Register, FigureStrip, Figure, DetailDrawer, Field, FormRow, Panel, money, fmtDate,
} from './design';

// Care management, month by month.
//
// Every Support at Home participant needs at least 15 minutes of DIRECT care management a month (with
// them or their registered supporter). Approved time is claimed from the provider's pooled care
// management account (10% of every quarterly budget) at the provider's hourly price, never from the
// participant's own budget, and never beyond what the account holds. Indirect work counts as care
// management but not towards the monthly minimum; some work (rostering, invoicing) is not claimed.

const INP = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const monthName = (k) => { const [y, m] = k.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }); };
const PERSON = [['participant', 'The participant'], ['supporter', 'Their registered supporter'], ['other', 'Someone else']];

export default function AgedCareCareManagement({ clientId: only = '', embedded = false } = {}) {
  const now = new Date();
  // The last twelve months: time is often logged late, and a correction can come months after.
  const months = Array.from({ length: 12 }, (_, i) => monthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
  const [month, setMonth] = useState(months[0]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // The participant row being logged for, or { name, entry } when an entry is being corrected.
  const [log, setLog] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [owners, setOwners] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [why, setWhy] = useState('');
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Entries not approved are loaded too, so they can be corrected and sent back for approval.
      const [r, p, x] = await Promise.all([
        api.get('/aged-care/care-management/month', { params: { month } }),
        api.get('/aged-care/care-management', { params: { month, status: 'pending' } }),
        api.get('/aged-care/care-management', { params: { month, status: 'rejected' } }),
      ]);
      const mine = (list) => (Array.isArray(list) ? list : []).filter((a) => !only || String(a.client_id) === String(only));
      setData(r.data); setPending(mine(p.data)); setRejected(mine(x.data)); setPicked(new Set());
    } catch (e) { setError(e.response?.data?.message || 'Could not load care management'); setData(null); }
    finally { setLoading(false); }
  }, [month, only]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/aged-care/attention/owners').then((r) => setOwners(r.data || [])).catch(() => {}); }, []);

  const types = (data && data.activityTypes) || [];
  const excluded = (data && data.excludedTypes) || [];
  const kindFor = (t) => (types.find((x) => x.type === t) || {}).kind;
  const open = (row) => {
    const today = isoDate();
    const first = types[0] ? types[0].type : 'Meeting with the participant';
    setLog(row);
    setForm({ date: today.slice(0, 7) === month ? today : `${month}-01`, type: first, kind: kindFor(first) || 'direct', minutes: '15',
      person: 'participant', contactName: '', partner: '', price: data?.hourlyPrice != null ? String(data.hourlyPrice) : (data?.lastRate ? String(data.lastRate) : ''),
      notes: '', approveNow: false });
  };
  // Correcting an entry before it is approved. Approved time is already a claim line, so the server
  // refuses it (409) and says to correct it on the statement.
  const openEdit = (a) => {
    const type = a.activity_type || (types[0] ? types[0].type : 'Meeting with the participant');
    setLog({ name: a.client_name || 'Participant', entry: a });
    setForm({ date: String(a.activity_date || '').slice(0, 10), type, kind: a.contact_kind || kindFor(type) || 'direct',
      minutes: a.minutes != null ? String(a.minutes) : '', person: a.contact_person || 'participant', contactName: a.contact_name || '',
      partner: '', price: Number(a.hourly_price) > 0 ? String(a.hourly_price) : '', notes: a.notes || '', approveNow: false });
  };
  const close = useCallback(() => setLog(null), []);
  const isExcluded = (t) => excluded.some((x) => x.toLowerCase() === String(t || '').toLowerCase());
  const save = async () => {
    const minutes = Number(form.minutes) || 0;
    if (minutes <= 0 && !isExcluded(form.type)) { toast.error('Enter the minutes spent'); return; }
    setSaving(true);
    if (log.entry) {
      try {
        // A blank price is sent as null on purpose: the entry then takes your hourly price from
        // Rates and fees when it is approved.
        await api.patch(`/aged-care/care-management/${log.entry.id}`, {
          activityDate: form.date, activityType: form.type, contactKind: form.kind, minutes,
          contactPerson: form.kind === 'direct' ? form.person : null, contactName: form.contactName || null,
          hourlyPrice: Number(form.price) > 0 ? Number(form.price) : null, notes: form.notes || null,
        });
        toast.success('Saved');
        setLog(null); load();
      } catch (e) { toast.error(e.response?.data?.message || 'Could not save the changes'); }
      finally { setSaving(false); }
      return;
    }
    try {
      const r = await api.post('/aged-care/care-management', {
        fundingId: log.fundingId, activityDate: form.date, activityType: form.type, contactKind: form.kind, minutes,
        contactPerson: form.kind === 'direct' ? form.person : null, contactName: form.contactName || null,
        carePartnerId: form.partner || undefined, hourlyPrice: Number(form.price) > 0 ? Number(form.price) : undefined,
        notes: form.notes || null, approveNow: !!form.approveNow,
      });
      const ap = r.data?.approval;
      if (r.data?.excludedReason) toast(r.data.excludedReason, { duration: 6000 });
      else if (ap && ap.approved.length) toast.success(`${minutes} minutes approved: ${money(ap.approved[0].amount)} to claim from the account`);
      else if (ap && ap.refused.length) toast.error(ap.refused[0].reason);
      else toast.success(`${minutes} minutes logged. Approve it to claim it.`);
      setLog(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not log the time'); }
    finally { setSaving(false); }
  };
  const approve = async (ids) => {
    if (!ids.length) return;
    try {
      const r = await api.post('/aged-care/care-management/approve', { ids });
      const a = r.data?.approved || []; const ref = r.data?.refused || [];
      if (a.length) toast.success(`${a.length} approved: ${money(a.reduce((n, x) => n + Number(x.amount || 0), 0))} to claim`);
      for (const x of ref.slice(0, 3)) toast.error(`${x.name || 'Entry'}: ${x.reason}`, { duration: 7000 });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not approve'); }
  };
  // Approving everything claims real money from the pooled account in one go, so it is asked first.
  const approveAll = async () => {
    const n = pending.length;
    if (!n) return;
    const ok = await confirm(
      `${n === 1 ? 'The entry' : `All ${n} entries`} waiting for approval will be claimed from your pooled care management account at the hourly price, never from the participant's budget. An entry the account cannot cover stays waiting.`,
      { title: n === 1 ? 'Approve 1 entry?' : `Approve ${n} entries?`, confirmLabel: `Approve ${n}` },
    );
    if (!ok) return;
    approve(pending.map((p) => p.id));
  };
  const reject = async (id) => {
    try { await api.post(`/aged-care/care-management/${id}/reject`, { reason: why }); toast.success('Not approved'); setRejecting(null); setWhy(''); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not update it'); }
  };

  const rows = ((data && data.rows) || []).filter((r) => !only || String(r.clientId) === String(only));
  const short = rows.filter((r) => !r.metMinimum);
  const minutes = rows.reduce((n, r) => n + r.minutes, 0);
  const claimable = rows.reduce((n, r) => n + r.claimable, 0);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const setType = (e) => { const t = e.target.value; setForm((p) => ({ ...p, type: t, kind: kindFor(t) || p.kind })); };
  const togglePick = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const columns = [
    { key: 'name', header: 'Participant', sortable: true, render: (r) => (
      <div className="min-w-0">
        <div className="font-medium text-slate-900">{r.name}</div>
        <div className="text-[12px] text-slate-500">Classification {r.level || '—'}{r.pending ? ` · ${r.pending} to approve` : ''}</div>
      </div>
    ) },
    { key: 'directMinutes', header: `Direct minutes in ${monthName(month).split(' ')[0]}`, width: 230, sortable: true, sortValue: (r) => r.directMinutes, render: (r) => (
      <div className="flex items-center gap-2.5 min-w-[10rem]">
        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
          <div className={clsx('h-full rounded-full', r.metMinimum ? 'hue-bar' : 'bg-amber-500')} style={{ width: `${Math.min(100, (r.directMinutes / 15) * 100)}%` }} />
        </div>
        <span className={clsx('w-16 text-right tabular-nums text-[13px] font-medium', r.metMinimum ? 'text-slate-800' : 'text-amber-700')}>{r.directMinutes} min</span>
      </div>
    ) },
    { key: 'activities', header: 'Activities', align: 'right', width: 90, render: (r) => <span className="tabular-nums">{r.activities}</span> },
    { key: 'claimable', header: 'Claimed', align: 'right', width: 110, sortable: true, sortValue: (r) => r.claimable, render: (r) => <span className="tabular-nums">{money(r.claimable)}</span> },
    { key: 'last', header: 'Last activity', width: 210, render: (r) => (r.last
      ? <span className="text-[13px] text-slate-600">{fmtDate(r.last.date)}, {r.last.type}</span>
      : <span className="text-[13px] text-slate-400">None yet</span>) },
    { key: 'act', header: <span className="sr-only">Log time</span>, width: 110, align: 'right', render: (r) => (
      <button type="button" onClick={(e) => { e.stopPropagation(); open(r); }} className="btn-secondary btn-sm">
        <Timer size={13} aria-hidden="true" /> Log time
      </button>
    ) },
  ];

  return (
    <div>
      {embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-[13px] text-slate-600 max-w-[60ch]">At least 15 minutes of direct care management a month. Approved time is claimed from your pooled care management account, never from their budget.</p>
          <select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month" className={clsx(INP, '!w-auto min-w-[200px]')}>
            {months.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
          </select>
        </div>
      ) : <>
      <PageHead icon={HeartHandshake}
        title="Care management"
        purpose="Every participant needs at least 15 minutes of direct care management a month. Approved time is claimed from your pooled care management account at your hourly price, never from the participant's budget."
        actions={
          <select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month" className={clsx(INP, '!w-auto min-w-[200px]')}>
            {months.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
          </select>
        }
      />

      <FigureStrip>
        <Figure label="Still under 15 direct minutes" value={short.length} tone={short.length ? 'amber' : 'green'} sub={short.length ? 'Need care management this month' : 'Everyone has their minimum'} />
        <Figure label="Minutes logged" value={minutes.toLocaleString('en-AU')} sub={monthName(month)} />
        <Figure label="Claimed from the account" value={money(claimable)} sub={pending.length ? `${pending.length} waiting for approval` : 'Nothing waiting'} />
        <Figure label="Account balance" value={data?.account ? money(data.account.balance) : '—'}
          sub={data?.account?.rollover ? `Year-end rollover ${fmtDate(data.account.rollover)}` : ''} tone={data?.account && data.account.balance < 0 ? 'red' : 'slate'} />
      </FigureStrip>
      </>}

      {pending.length > 0 && (
        <Panel title={`Waiting for approval (${pending.length})`} icon={CheckCircle2} className="mb-5" bodyClass="divide-y divide-slate-100"
          actions={(
            <span className="flex gap-2">
              <button type="button" className="btn-secondary btn-sm" disabled={!picked.size} onClick={() => approve([...picked])}>Approve selected</button>
              <button type="button" className="btn-primary btn-sm" onClick={approveAll}>Approve all</button>
            </span>
          )}>
          {data && data.hourlyPrice == null && (
            <p className="px-4 py-2 text-[12px] text-amber-800">Set your care management price per hour under Rates and fees, or entries without their own price cannot be approved.</p>
          )}
          {pending.map((a) => (
            <div key={a.id} className="px-4 py-2.5 text-[13px] flex flex-wrap items-center justify-between gap-2">
              <label className="flex items-start gap-2 cursor-pointer min-w-0">
                <input type="checkbox" className="mt-1" checked={picked.has(a.id)} onChange={() => togglePick(a.id)} />
                <span className="min-w-0">
                  <span className="text-slate-900 font-medium">{a.client_name || 'Participant'}</span>
                  <span className="text-slate-500"> · {fmtDate(a.activity_date)} · {a.activity_type} · {a.contact_kind === 'direct' ? 'direct' : 'indirect'} · {a.minutes} min{a.care_partner ? ` · ${a.care_partner}` : ''}</span>
                  {a.notes && <span className="block text-slate-500">{a.notes}</span>}
                </span>
              </label>
              {rejecting === a.id ? (
                <span className="flex items-center gap-2">
                  <input aria-label="Why not approved" autoFocus className="input-field !w-56 !py-1 !text-[12px]" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Why it is not approved" />
                  <button type="button" className="btn-secondary btn-sm" disabled={!why.trim()} onClick={() => reject(a.id)}>Save</button>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => { setRejecting(null); setWhy(''); }}>Cancel</button>
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <button type="button" className="btn-secondary btn-sm" onClick={() => approve([a.id])}>Approve</button>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => openEdit(a)}><Pencil size={13} aria-hidden="true" /> Edit</button>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => { setRejecting(a.id); setWhy(''); }}>Not approved</button>
                </span>
              )}
            </div>
          ))}
        </Panel>
      )}

      {rejected.length > 0 && (
        <Panel title={`Not approved (${rejected.length})`} icon={XCircle} className="mb-5" bodyClass="divide-y divide-slate-100">
          {rejected.map((a) => (
            <div key={a.id} className="px-4 py-2.5 text-[13px] flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0">
                <span className="text-slate-900 font-medium">{a.client_name || 'Participant'}</span>
                <span className="text-slate-500"> · {fmtDate(a.activity_date)} · {a.activity_type} · {a.minutes} min{a.care_partner ? ` · ${a.care_partner}` : ''}</span>
                {a.reject_reason && <span className="block text-slate-500">Why: {a.reject_reason}</span>}
              </span>
              <button type="button" className="btn-secondary btn-sm" onClick={() => openEdit(a)}><Pencil size={13} aria-hidden="true" /> Edit</button>
            </div>
          ))}
        </Panel>
      )}

      <Register
        title="Participants" icon={HeartHandshake} count={rows.length} countLabel={rows.length === 1 ? 'participant' : 'participants'}
        columns={columns} rows={[...rows].sort((a, b) => Number(a.metMinimum) - Number(b.metMinimum) || a.name.localeCompare(b.name))}
        rowKey="clientId" loading={loading} error={error} onRetry={load}
        rowTone={(r) => (r.metMinimum ? undefined : 'amber')}
        onRowClick={(r) => open(r)}
        empty={{ icon: HeartHandshake, title: 'No Support at Home participants', hint: 'Participants with a Support at Home funding plan appear here.' }}
        footer="Participants under the monthly minimum are listed first. Only direct care management (with the participant or their registered supporter) counts towards the 15 minutes."
      />

      <DetailDrawer
        open={!!log} onClose={close} width="md" icon={Timer}
        title={log ? (log.entry ? 'Edit care management' : `Log care management for ${log.name}`) : 'Log care management'}
        subtitle={log ? (log.entry
          ? `${log.name}, ${log.entry.status === 'rejected' ? 'not approved' : 'waiting for approval'}`
          : `${log.directMinutes} direct minutes in ${monthName(month)}`) : ''}
        footer={<>
          <button type="button" onClick={close} className="btn-secondary text-sm">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm">{saving ? 'Saving…' : (log && log.entry ? 'Save changes' : 'Log time')}</button>
        </>}
      >
        {log && (
          <div className="space-y-4">
            {log.entry && log.entry.status === 'rejected' && (
              <div className="text-[13px] text-slate-600 space-y-0.5">
                {log.entry.reject_reason && <p>Why it was not approved: {log.entry.reject_reason}</p>}
                <p>Saving sends it back for approval.</p>
              </div>
            )}
            <FormRow cols={2}>
              <Field label="Date">{(id) => <input id={id} type="date" value={form.date} onChange={set('date')} className={INP} />}</Field>
              <Field label="Activity">{(id) => (
                <select id={id} value={form.type} onChange={setType} className={INP}>
                  {/* An entry logged under an activity no longer on the list keeps it as a choice. */}
                  {form.type && !types.some((t) => t.type === form.type) && !excluded.includes(form.type) && <option>{form.type}</option>}
                  <optgroup label="Direct (counts towards the 15 minutes)">{types.filter((t) => t.kind === 'direct').map((t) => <option key={t.type}>{t.type}</option>)}</optgroup>
                  <optgroup label="Indirect">{types.filter((t) => t.kind === 'indirect').map((t) => <option key={t.type}>{t.type}</option>)}</optgroup>
                  <optgroup label="Not claimed as care management">{excluded.map((t) => <option key={t}>{t}</option>)}</optgroup>
                </select>
              )}</Field>
            </FormRow>
            {isExcluded(form.type) ? (
              <p className="text-[13px] text-amber-800">This is not claimed as care management. It is logged for the record only.</p>
            ) : (
              <>
                <FormRow cols={2}>
                  <Field label="Minutes">{(id) => (
                    <input id={id} type="number" min="1" step="5" inputMode="numeric" value={form.minutes} onChange={set('minutes')} className={INP} />
                  )}</Field>
                  <Field label="Price per hour" hint="Your care management price. Part hours are claimed.">{(id) => (
                    <input id={id} type="number" min="0" step="0.01" inputMode="decimal" value={form.price} onChange={set('price')} className={INP} placeholder="From Rates and fees" />
                  )}</Field>
                </FormRow>
                {form.kind === 'direct' && (
                  <FormRow cols={2}>
                    <Field label="With">{(id) => (
                      <select id={id} value={form.person} onChange={set('person')} className={INP}>{PERSON.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                    )}</Field>
                    {form.person !== 'participant' && (
                      <Field label="Their name">{(id) => <input id={id} value={form.contactName} onChange={set('contactName')} className={INP} />}</Field>
                    )}
                  </FormRow>
                )}
              </>
            )}
            {/* Who did the work is set when the time is logged; a correction keeps it. */}
            {!log.entry && (
              <Field label="Care partner" hint="Who did the work. Leave blank for yourself.">{(id) => (
                <select id={id} value={form.partner} onChange={set('partner')} className={INP}>
                  <option value="">Me</option>
                  {owners.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed user'}</option>)}
                </select>
              )}</Field>
            )}
            <Field label="Notes">{(id) => <textarea id={id} rows={3} value={form.notes} onChange={set('notes')} className={INP} placeholder="What was discussed or arranged" />}</Field>
            {!isExcluded(form.type) && Number(form.minutes) > 0 && Number(form.price) > 0 && (
              <p className="text-[13px] text-slate-600">To claim from the care management account once approved: <span className="font-semibold tabular-nums">{money((Number(form.minutes) / 60) * Number(form.price))}</span></p>
            )}
            {!isExcluded(form.type) && !log.entry && (
              <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!form.approveNow} onChange={set('approveNow')} /> Approve it now
              </label>
            )}
          </div>
        )}
      </DetailDrawer>
    </div>
  );
}
